// Utilidades pequeñas del módulo de tutoriales / Ayuda.

export type Lang = "es" | "en";

export const langOf = (language: string | undefined): Lang =>
  (language || "es").startsWith("en") ? "en" : "es";

/**
 * Las capturas viven en la wiki (`apps/landing/public/docs/...`, y las de
 * Sistemas Internos en `apps/landing/docs-internal-assets/...`, fuera del sitio
 * público) y v5 las sirve bajo la misma ruta `/docs/...` (plugin
 * `vite-plugin-docs-assets`). Se resuelve
 * contra la base de la app para que funcione también en las vistas previas.
 */
export function assetUrl(src: string): string {
  if (/^https?:\/\//.test(src)) return src;
  const base = (import.meta.env.BASE_URL || "/").replace(/\/?$/, "/");
  return `${base}${src.replace(/^\//, "")}`;
}

/** Id del usuario de la sesión (el store no tipa `user`). */
export function currentUserId(user: unknown): string | null {
  const u = (user || {}) as { id?: string; uid?: string; userId?: string };
  const id = u.id || u.uid || u.userId || (user ? localStorage.getItem("userId") : null);
  return id ? String(id) : null;
}

/**
 * ¿El evento de teclado viene de un campo editable (input, textarea, select o
 * contenteditable)? Ahí las flechas mueven el cursor, no el tutorial.
 */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as HTMLElement).tagName !== "string") return false;
  const el = target as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (el.isContentEditable === true) return true;
  return typeof el.closest === "function" && el.closest("[contenteditable]:not([contenteditable='false'])") !== null;
}

/** "AAAA-MM-DD" (día LOCAL) de una fecha ISO, para el <input type="date">. */
export function isoToLocalDay(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Medianoche LOCAL del día elegido, en ISO (lo que guarda la API). */
export function localDayToIso(day: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const [y, m, d] = day.split("-").map(Number);
  const date = new Date(y, m - 1, d, 0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** Largo máximo del texto de un paso antes de «Ver más». */
export const PREVIEW_MAX = 100;

/**
 * Recorta el texto del paso a `max` caracteres (contando el «…») sin partir
 * palabras: corta en el último espacio antes del límite. Si el texto ya cabe,
 * lo devuelve tal cual con `truncated: false`.
 */
export function previewText(text: string, max = PREVIEW_MAX): { text: string; truncated: boolean } {
  const clean = (text || "").trim();
  if (clean.length <= max) return { text: clean, truncated: false };
  const room = clean.slice(0, max - 1);
  const lastSpace = room.search(/\s\S*$/);
  const cut = (lastSpace > max * 0.6 ? room.slice(0, lastSpace) : room).replace(/[\s,;:.\-–—]+$/, "");
  return { text: `${cut}…`, truncated: true };
}
